import {
  LOG_LEVEL,
  Purchases,
  type CustomerInfo,
  type PurchasesOffering,
  type PurchasesOfferings,
  type PurchasesPackage,
} from '@revenuecat/purchases-capacitor';
import { Capacitor } from '@capacitor/core';
import { isBetaRevenueCatBypassEnabled } from '@/core/betaAccess';

// RevenueCat API keys
// These are PUBLIC keys safe to include in client code
const REVENUECAT_IOS_API_KEY = import.meta.env.VITE_REVENUECAT_IOS_API_KEY || 'appl_rAMRmDSNbjfbZXJBhSBhBchOESF';
const REVENUECAT_ANDROID_API_KEY = import.meta.env.VITE_REVENUECAT_ANDROID_API_KEY || '';

let isConfigured = false;
let configurePromise: Promise<void> | null = null;
let readyForUserPromise: Promise<void> | null = null;
let configuredAppUserId: string | null = null;
let customerInfoListenerId: string | null = null;
let cachedPremiumStatus: PremiumStatus | null = null;
const premiumStatusSubscribers = new Set<(status: PremiumStatus) => void>();
const PREMIUM_ENTITLEMENT_ID = 'pro';
const E2E_PREMIUM_STATUS_KEY = '__E2E_PREMIUM_STATUS__';
const E2E_PLATFORM_KEY = '__E2E_REVENUECAT_PLATFORM__';

const EMPTY_PREMIUM_STATUS: PremiumStatus = {
  customerInfo: null,
  entitlements: [],
  expirationDate: null,
  isActive: false,
  productIdentifier: null,
  willRenew: false,
};

const BETA_PREMIUM_STATUS: PremiumStatus = {
  customerInfo: null,
  entitlements: ['pro'],
  expirationDate: null,
  isActive: true,
  productIdentifier: 'beta_access',
  willRenew: false,
};

export interface PremiumStatus {
  customerInfo: CustomerInfo | null
  entitlements: string[]
  expirationDate: string | null
  isActive: boolean
  productIdentifier: string | null
  willRenew: boolean
}

// ============================================
// E2E Test Mock Support
// ============================================

/**
 * Check for E2E test premium status override.
 * Only active when window.__TEST_PREMIUM_STATUS__ is set by Playwright.
 */
function getTestPremiumOverride(): PremiumStatus | null {
  if (typeof window === 'undefined') {
    return null;
  }

  let override: any = (window as any).__TEST_PREMIUM_STATUS__;
  if (!override) {
    const serialized = window.localStorage.getItem(E2E_PREMIUM_STATUS_KEY);
    if (serialized) {
      try {
        override = JSON.parse(serialized);
      } catch (error) {
        console.warn('RevenueCat: Failed to parse E2E premium status from localStorage', error);
      }
    }
  }

  if (override) {
    return {
      customerInfo: null,
      entitlements: override.isActive ? ['pro'] : [],
      expirationDate: override.expirationDate ?? null,
      isActive: override.isActive ?? false,
      productIdentifier: override.productIdentifier ?? null,
      willRenew: false,
    };
  }
  return null;
}

// ============================================
// RevenueCat Core
// ============================================

const getPremiumEntitlement = (customerInfo: CustomerInfo | null) =>
  customerInfo?.entitlements.active[PREMIUM_ENTITLEMENT_ID] ?? null;

const toPremiumStatus = (customerInfo: CustomerInfo | null): PremiumStatus => {
  const premiumEntitlement = getPremiumEntitlement(customerInfo);

  return {
    customerInfo,
    entitlements: customerInfo ? Object.keys(customerInfo.entitlements.active) : [],
    expirationDate: premiumEntitlement?.expirationDate ?? null,
    isActive: !!premiumEntitlement,
    productIdentifier: premiumEntitlement?.productIdentifier ?? null,
    willRenew: premiumEntitlement?.willRenew ?? false,
  };
};

const publishPremiumStatus = (status: PremiumStatus) => {
  cachedPremiumStatus = status;
  premiumStatusSubscribers.forEach((listener) => listener(status));
};

export function isRevenueCatSupportedPlatform(): boolean {
  if (isBetaRevenueCatBypassEnabled()) {
    return false;
  }

  if (typeof window !== 'undefined') {
    const forcedPlatform = window.localStorage.getItem(E2E_PLATFORM_KEY);
    if (forcedPlatform === 'ios' || forcedPlatform === 'true') {
      return true;
    }
  }

  return Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios';
}

async function ensureCustomerInfoListener(): Promise<void> {
  if (!isConfigured || customerInfoListenerId || !isRevenueCatSupportedPlatform()) {
    return;
  }

  customerInfoListenerId = await Purchases.addCustomerInfoUpdateListener((customerInfo) => {
    publishPremiumStatus(toPremiumStatus(customerInfo));
  });
}

/**
 * Initialize RevenueCat SDK.
 * This should only be called once, typically during app startup.
 */
export async function configureRevenueCat(userId?: string): Promise<void> {
  if (isBetaRevenueCatBypassEnabled()) {
    publishPremiumStatus(BETA_PREMIUM_STATUS);
    return;
  }

  if (isConfigured) {
    console.log('RevenueCat already configured');
    await ensureCustomerInfoListener();
    return;
  }

  if (!isRevenueCatSupportedPlatform()) {
    console.log('RevenueCat: Skipping unsupported platform');
    return;
  }

  const platform = Capacitor.getPlatform();
  const apiKey = platform === 'ios' ? REVENUECAT_IOS_API_KEY : REVENUECAT_ANDROID_API_KEY;

  if (!apiKey) {
    console.warn(`RevenueCat: No API key configured for ${platform}`);
    return;
  }

  try {
    // Enable debug logging
    await Purchases.setLogLevel({ level: LOG_LEVEL.DEBUG });

    console.log('RevenueCat: Configuring with key:', apiKey);
    await Purchases.configure({
      apiKey,
      appUserID: userId || undefined,
    });
    isConfigured = true;
    configuredAppUserId = userId ?? null;
    await ensureCustomerInfoListener();
    console.log('RevenueCat configured successfully');
  } catch (error) {
    console.error('RevenueCat configuration error:', error);
    throw error;
  }
}

/**
 * Ensure RevenueCat is configured before any API calls.
 */
async function ensureConfigured(userId?: string): Promise<boolean> {
  if (isBetaRevenueCatBypassEnabled()) {
    publishPremiumStatus(BETA_PREMIUM_STATUS);
    return false;
  }

  if (isConfigured) return true;

  if (!isRevenueCatSupportedPlatform()) return false;

  if (configurePromise) {
    await configurePromise;
    return isConfigured;
  }

  configurePromise = configureRevenueCat(userId);

  try {
    await configurePromise;
  } finally {
    configurePromise = null;
  }

  return isConfigured;
}

export async function prepareRevenueCatForUser(userId: string): Promise<void> {
  if (isBetaRevenueCatBypassEnabled()) {
    publishPremiumStatus(BETA_PREMIUM_STATUS);
    return;
  }

  if (!await ensureConfigured(userId)) return;

  if (configuredAppUserId === userId && !readyForUserPromise) {
    await ensureCustomerInfoListener();
    return;
  }

  if (readyForUserPromise) {
    await readyForUserPromise;
    if (configuredAppUserId === userId) {
      return;
    }
  }

  const nextReadyPromise = (async () => {
    if (configuredAppUserId !== userId) {
      const { customerInfo } = await Purchases.logIn({ appUserID: userId });
      configuredAppUserId = userId;
      publishPremiumStatus(toPremiumStatus(customerInfo));
    }

    await ensureCustomerInfoListener();
  })();

  readyForUserPromise = nextReadyPromise;

  try {
    await nextReadyPromise;
  } finally {
    if (readyForUserPromise === nextReadyPromise) {
      readyForUserPromise = null;
    }
  }
}

/**
 * Log in a user to RevenueCat (call after Firebase auth).
 */
export async function loginRevenueCat(userId: string): Promise<CustomerInfo | null> {
  try {
    await prepareRevenueCatForUser(userId);
    return cachedPremiumStatus?.customerInfo ?? null;
  } catch (error) {
    console.error('RevenueCat login error:', error);
    return null;
  }
}

/**
 * Log out from RevenueCat.
 */
export async function logoutRevenueCat(): Promise<void> {
  if (isBetaRevenueCatBypassEnabled()) {
    configuredAppUserId = null;
    readyForUserPromise = null;
    cachedPremiumStatus = null;
    return;
  }

  if (!isRevenueCatSupportedPlatform()) return;

  try {
    if (customerInfoListenerId) {
      await Purchases.removeCustomerInfoUpdateListener({ listenerToRemove: customerInfoListenerId });
      customerInfoListenerId = null;
    }
    await Purchases.logOut();
    configuredAppUserId = null;
    readyForUserPromise = null;
    cachedPremiumStatus = null;
  } catch (error) {
    console.error('RevenueCat logout error:', error);
  }
}

/**
 * Get the current customer info from RevenueCat.
 */
export async function getCustomerInfo(): Promise<CustomerInfo | null> {
  if (isBetaRevenueCatBypassEnabled()) {
    publishPremiumStatus(BETA_PREMIUM_STATUS);
    return null;
  }

  if (!await ensureConfigured()) return null;

  try {
    const { customerInfo } = await Purchases.getCustomerInfo();
    publishPremiumStatus(toPremiumStatus(customerInfo));
    return customerInfo;
  } catch (error) {
    console.error('RevenueCat getCustomerInfo error:', error);
    return null;
  }
}

/**
 * Get available offerings (products/packages).
 */
export async function getOfferings(): Promise<PurchasesOfferings | null> {
  if (isBetaRevenueCatBypassEnabled()) {
    console.log('RevenueCat: getOfferings skipped - beta bypass enabled');
    return null;
  }

  if (!await ensureConfigured()) {
    console.log('RevenueCat: getOfferings skipped - not configured');
    return null;
  }

  try {
    console.log('RevenueCat: Fetching offerings...');
    const offerings = await Purchases.getOfferings();
    console.log('RevenueCat offerings result:', {
      current: offerings?.current?.identifier,
      allKeys: offerings?.all ? Object.keys(offerings.all) : [],
      raw: offerings,
    });
    return offerings;
  } catch (error) {
    console.error('RevenueCat getOfferings error:', error);
    return null;
  }
}

export async function getCurrentOffering(): Promise<PurchasesOffering | null> {
  const offerings = await getOfferings();

  if (offerings?.current) {
    return offerings.current;
  }

  if (offerings?.all?.['default']) {
    return offerings.all['default'];
  }

  const allOfferings = offerings?.all ? Object.values(offerings.all) : [];
  if (allOfferings.length > 0) {
    return allOfferings[0];
  }

  return null;
}

export async function purchaseRevenueCatPackage(
  packageToPurchase: PurchasesPackage
): Promise<CustomerInfo | null> {
  if (isBetaRevenueCatBypassEnabled()) {
    publishPremiumStatus(BETA_PREMIUM_STATUS);
    return null;
  }

  if (!await ensureConfigured()) {
    throw new Error('Subscriptions are only available in the iOS app');
  }

  try {
    const { customerInfo } = await Purchases.purchasePackage({ aPackage: packageToPurchase });
    publishPremiumStatus(toPremiumStatus(customerInfo));
    return customerInfo;
  } catch (error: any) {
    if (error?.code === 'PURCHASE_CANCELLED') {
      return null;
    }

    console.error('RevenueCat purchasePackage error:', error);
    throw error;
  }
}

export async function restoreRevenueCatPurchases(): Promise<CustomerInfo | null> {
  if (isBetaRevenueCatBypassEnabled()) {
    publishPremiumStatus(BETA_PREMIUM_STATUS);
    return null;
  }

  if (!await ensureConfigured()) return null;

  try {
    const { customerInfo } = await Purchases.restorePurchases();
    publishPremiumStatus(toPremiumStatus(customerInfo));
    return customerInfo;
  } catch (error) {
    console.error('RevenueCat restorePurchases error:', error);
    return null;
  }
}

/**
 * Get premium status - checks E2E test override first, then RevenueCat.
 */
export async function getPremiumStatus(): Promise<PremiumStatus> {
  const testOverride = getTestPremiumOverride();
  if (testOverride) {
    console.log('RevenueCat: Using E2E test override', testOverride);
    return testOverride;
  }

  if (isBetaRevenueCatBypassEnabled()) {
    publishPremiumStatus(BETA_PREMIUM_STATUS);
    return BETA_PREMIUM_STATUS;
  }

  const customerInfo = await getCustomerInfo();
  if (!customerInfo) {
    return cachedPremiumStatus ?? EMPTY_PREMIUM_STATUS;
  }

  return toPremiumStatus(customerInfo);
}

/**
 * Check if user has active pro entitlement.
 */
export async function hasActivePremium(): Promise<boolean> {
  const premiumStatus = await getPremiumStatus();
  return premiumStatus.isActive;
}

/**
 * Check if SDK is configured (for debugging).
 */
export function isSDKConfigured(): boolean {
  return isConfigured;
}

/**
 * Get the API key being used (for debugging).
 */
export function getApiKey(): string {
  return REVENUECAT_IOS_API_KEY;
}

export function getCachedPremiumStatus(): PremiumStatus | null {
  return getTestPremiumOverride() ?? (isBetaRevenueCatBypassEnabled() ? BETA_PREMIUM_STATUS : cachedPremiumStatus);
}

export function hasResolvedPremiumStatus(): boolean {
  return getCachedPremiumStatus() !== null;
}

export function subscribeToPremiumStatus(
  listener: (status: PremiumStatus) => void
): () => void {
  premiumStatusSubscribers.add(listener);

  const currentStatus = getCachedPremiumStatus();
  if (currentStatus) {
    listener(currentStatus);
  }

  return () => {
    premiumStatusSubscribers.delete(listener);
  };
}

/**
 * Export singleton for convenience.
 *
 * Do not call restorePurchases automatically on app launch. RevenueCat expects
 * restores to be user initiated because they can trigger App Store account UI.
 * For startup checks, use the configured user session + getCustomerInfo instead.
 */
export const revenueCat = {
  configure: configureRevenueCat,
  login: loginRevenueCat,
  logout: logoutRevenueCat,
  prepareForUser: prepareRevenueCatForUser,
  getCustomerInfo,
  getOfferings,
  getCurrentOffering,
  getPremiumStatus,
  getCachedPremiumStatus,
  hasResolvedPremiumStatus,
  subscribeToPremiumStatus,
  isSupportedPlatform: isRevenueCatSupportedPlatform,
  isConfigured: isSDKConfigured,
  getApiKey,
  purchasePackage: purchaseRevenueCatPackage,
  restorePurchases: restoreRevenueCatPurchases,
  hasActivePremium,
};
